import React, { useState, useEffect } from 'react';
import { courseEventService } from '../../services/courseEventService';
import { trainingEventService } from '../../services/trainingEventService';
import { employeeCoursesSummaryService } from '../../services/employeeCoursesSummaryService';
import { useNotifyDataChange } from '../../hooks/useNotifyDataChange';
import './EnrollToEventModal.css';

const EnrollToEventModal = ({ isOpen, onClose, courseId, courseName, employee, employees, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const { notifyEnrollmentCreated } = useNotifyDataChange();

  // Определяем, массовая ли запись и список сотрудников
  const hasEmployeesArray = employees && employees.length > 0;
  const isBulkEnroll = employees && employees.length > 1;
  
  let targetEmployees = [];
  if (hasEmployeesArray) {
    targetEmployees = employees;
  } else if (employee) {
    targetEmployees = [employee];
  }

  useEffect(() => {
    if (isOpen && courseId) {
      loadEvents();
    }
  }, [isOpen, courseId]);

  // Используем единый метод сервиса для загрузки мероприятий
  const loadEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      // Используем существующий метод сервиса, который уже содержит всю логику фильтрации
      const availableEvents = await courseEventService.getUpcomingEvents(courseId);
      
      setEvents(availableEvents);
      if (availableEvents.length > 0) {
        setSelectedEvent(availableEvents[0]);
      }
    } catch (err) {
      setError('Не удалось загрузить список мероприятий');
      console.error('Ошибка загрузки мероприятий:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    if (!selectedEvent) {
      setError('Пожалуйста, выберите мероприятие');
      return;
    }

    if (targetEmployees.length === 0) {
      setError('Нет сотрудников для записи');
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const employeeIds = targetEmployees.map(emp => emp.employeeId);
      
      await trainingEventService.addParticipants(selectedEvent.id, employeeIds, 'confirmed');
      
      // Формируем сообщение об успехе
      let message;
      if (targetEmployees.length > 1) {
        const names = targetEmployees.map(emp => emp.employeeName).join(', ');
        message = `${targetEmployees.length} сотрудников (${names}) успешно записаны на мероприятие "${selectedEvent.title}"`;
      } else {
        const empName = targetEmployees[0].employeeName;
        message = `Сотрудник ${empName} успешно записан на мероприятие "${selectedEvent.title}"`;
      }
      
      setSuccessMessage(message);
      
      // Инвалидируем кеш мероприятий курса и сводки
      courseEventService.invalidateCache(courseId);
      employeeCoursesSummaryService.invalidateCache();
      
      // Уведомляем приложение о создании записи
      notifyEnrollmentCreated({
        eventId: selectedEvent.id,
        eventTitle: selectedEvent.title,
        courseId: courseId,
        courseName: courseName,
        employees: targetEmployees,
        isBulkEnroll: targetEmployees.length > 1,
        timestamp: new Date().toISOString()
      });
      
      // Вызываем колбэк успеха
      setTimeout(() => {
        if (onSuccess) {
          onSuccess({ 
            ...selectedEvent, 
            course_id: courseId
          }, targetEmployees);
        }
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.message || 'Не удалось записать сотрудников на мероприятие');
      console.error('Ошибка записи:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Дата не указана';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateRange = (startDate, endDate) => {
    const start = formatDate(startDate);
    const end = formatDate(endDate);
    if (start === end) return start;
    return `${start} - ${end}`;
  };

  if (!isOpen) return null;

  return (
    <div className="enroll-modal-overlay" onClick={onClose}>
      <div className="enroll-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="enroll-modal-header">
          <h3>
            {targetEmployees.length > 1 
              ? `Массовая запись на обучение (${targetEmployees.length} чел.)` 
              : 'Запись на обучение'}
          </h3>
          <button className="enroll-modal-close" onClick={onClose} disabled={submitting}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="enroll-modal-body">
          {/* Информационный блок */}
          {targetEmployees.length > 1 ? (
            <div className="enroll-info-block">
              <div className="enroll-info-row">
                <span className="enroll-info-label">Сотрудников:</span>
                <span className="enroll-info-value">{targetEmployees.length} человек</span>
              </div>
              <details className="enroll-employees-details">
                <summary>Показать список</summary>
                <ul className="enroll-employees-list">
                  {targetEmployees.map(emp => (
                    <li key={emp.employeeId}>
                      {emp.employeeName}
                      {emp.department ? ` (${emp.department})` : ''}
                    </li>
                  ))}
                </ul>
              </details>
              <div className="enroll-info-row">
                <span className="enroll-info-label">Курс:</span>
                <span className="enroll-info-value">{courseName}</span>
              </div>
            </div>
          ) : targetEmployees.length === 1 ? (
            <div className="enroll-info-block">
              <div className="enroll-info-row">
                <span className="enroll-info-label">Сотрудник:</span>
                <span className="enroll-info-value">{targetEmployees[0].employeeName}</span>
              </div>
              {targetEmployees[0].department && (
                <div className="enroll-info-row">
                  <span className="enroll-info-label">Подразделение:</span>
                  <span className="enroll-info-value">{targetEmployees[0].department}</span>
                </div>
              )}
              {targetEmployees[0].position && (
                <div className="enroll-info-row">
                  <span className="enroll-info-label">Должность:</span>
                  <span className="enroll-info-value">{targetEmployees[0].position}</span>
                </div>
              )}
              <div className="enroll-info-row">
                <span className="enroll-info-label">Курс:</span>
                <span className="enroll-info-value">{courseName}</span>
              </div>
            </div>
          ) : (
            <div className="enroll-info-block">
              <div className="enroll-info-row">
                <span className="enroll-info-label">Курс:</span>
                <span className="enroll-info-value">{courseName}</span>
              </div>
            </div>
          )}

          {/* Сообщение об успехе */}
          {successMessage && (
            <div className="enroll-success-message">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <span>{successMessage}</span>
            </div>
          )}

          {/* Ошибка */}
          {error && !successMessage && (
            <div className="enroll-error-message">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* Загрузка мероприятий */}
          {loading && !successMessage && (
            <div className="enroll-loading">
              <div className="enroll-spinner"></div>
              <span>Загрузка доступных мероприятий...</span>
            </div>
          )}

          {/* Список мероприятий */}
          {!loading && !successMessage && events.length === 0 && (
            <div className="enroll-no-events">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <p>Нет доступных мероприятий для записи</p>
              <p className="enroll-no-events-hint">
                Проверьте позже или обратитесь к администратору
              </p>
            </div>
          )}

          {/* Выбор мероприятия */}
          {!loading && !successMessage && events.length > 0 && (
            <div className="enroll-events-list">
              <label className="enroll-events-label">Выберите дату обучения:</label>
              <div className="enroll-events-options">
                {events.map(event => (
                  <div
                    key={event.id}
                    className={`enroll-event-option ${selectedEvent?.id === event.id ? 'selected' : ''}`}
                    onClick={() => setSelectedEvent(event)}
                  >
                    <div className="enroll-event-radio">
                      <div className={`enroll-radio-circle ${selectedEvent?.id === event.id ? 'checked' : ''}`}>
                        {selectedEvent?.id === event.id && <div className="enroll-radio-inner"></div>}
                      </div>
                    </div>
                    <div className="enroll-event-info">
                      <div className="enroll-event-title">{event.title}</div>
                      <div className="enroll-event-dates">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                        <span>{formatDateRange(event.startDate, event.endDate)}</span>
                      </div>
                      <div className="enroll-event-meta">
                        <span className={`enroll-format-badge enroll-format-${event.format}`}>
                          {event.formatLabel}
                        </span>
                        <span className="enroll-location">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                            <circle cx="12" cy="10" r="3" />
                          </svg>
                          {event.location || 'Место не указано'}
                        </span>
                        <span className="enroll-slots">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                          Свободно мест: {event.availableSlots} / {event.maxParticipants}
                        </span>
                        {event.availableSlots <= 3 && event.availableSlots > 0 && (
                          <span className="enroll-slots-warning">
                            ⚠️ Осталось мало мест!
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="enroll-modal-footer">
          <button 
            className="enroll-btn-secondary" 
            onClick={onClose}
            disabled={submitting}
          >
            Отмена
          </button>
          <button 
            className="enroll-btn-primary" 
            onClick={handleEnroll}
            disabled={loading || events.length === 0 || submitting || !!successMessage}
          >
            {submitting ? (
              <>
                <div className="enroll-btn-spinner"></div>
                {targetEmployees.length > 1 ? `Запись ${targetEmployees.length} сотрудников...` : 'Запись...'}
              </>
            ) : (
              targetEmployees.length > 1 
                ? `Записать ${targetEmployees.length} сотрудников` 
                : 'Записать на обучение'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnrollToEventModal;